import { MutationFunctionOptions } from '@apollo/client/react/types/types';
import { useToast } from '@chakra-ui/react';
import {
  useInternalCreateOrUpdateIngestionFolderValidationFeedbackMutation,
  useInternalGetIngestionFolderValidationFeedbackQuery,
} from '@client/graphql/__generated__/document-operations';
import {
  useExternalCreateOrUpdateIngestionFolderValidationFeedbackMutation,
  useExternalGetIngestionFolderValidationFeedbackQuery,
} from '@client/graphql/__generated__/main-operations';
import { FeedbackType } from '@client/graphql/__generated__/types';
import { useCallback, useMemo } from 'react';
import { ComplianceValidationVariant } from '../ComplianceDrawer';

type UseIngestionFolderFeedbackProps = {
  ingestionFolderValidationId: string;
  variant: ComplianceValidationVariant;
};

export const useIngestionFolderFeedback = ({
  ingestionFolderValidationId,
  variant,
}: UseIngestionFolderFeedbackProps) => {
  const toast = useToast();

  const isExternal = variant === 'external';

  // External feedback query
  const { data: externalFeedbackData, refetch: refetchExternalFeedback } =
    useExternalGetIngestionFolderValidationFeedbackQuery({
      variables: {
        input: {
          ingestionFolderValidationId,
        },
      },
      skip: !ingestionFolderValidationId || !isExternal,
    });

  // Internal feedback query
  const { data: internalFeedbackData, refetch: refetchInternalFeedback } =
    useInternalGetIngestionFolderValidationFeedbackQuery({
      variables: {
        input: {
          ingestionFolderValidationId,
        },
      },
      skip: !ingestionFolderValidationId || isExternal,
    });

  const existingFeedback = useMemo(() => {
    if (isExternal) {
      return externalFeedbackData
        ?.externalGetIngestionFolderValidationFeedback?.[0];
    } else {
      return internalFeedbackData
        ?.internalGetIngestionFolderValidationFeedback?.[0];
    }
  }, [externalFeedbackData, internalFeedbackData, isExternal]);

  const handleFeedbackUpdate = useCallback(() => {
    if (isExternal) {
      void refetchExternalFeedback();
    } else {
      void refetchInternalFeedback();
    }
  }, [refetchExternalFeedback, refetchInternalFeedback, isExternal]);

  const [
    createOrUpdateExternalFeedback,
    { loading: createOrUpdateExternalFeedbackLoading },
  ] = useExternalCreateOrUpdateIngestionFolderValidationFeedbackMutation();

  const [
    createOrUpdateInternalFeedback,
    { loading: createOrUpdateInternalFeedbackLoading },
  ] = useInternalCreateOrUpdateIngestionFolderValidationFeedbackMutation();

  const createOrUpdateFeedback = useCallback(
    (
      feedback: {
        ingestionFolderValidationId: string;
        feedbackType: FeedbackType;
        comment?: string;
      },
      onFinish?: () => void
    ) => {
      const toastOptions: MutationFunctionOptions = {
        onCompleted: () => {
          toast({
            title: 'Feedback submitted',
            description: 'Your feedback has been saved successfully.',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          handleFeedbackUpdate();
          onFinish?.();
        },
        onError: (error) => {
          toast({
            title: 'Error submitting feedback',
            description: error.message,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        },
      };

      if (isExternal) {
        void createOrUpdateExternalFeedback({
          variables: { input: feedback },
          onCompleted: toastOptions.onCompleted,
          onError: toastOptions.onError,
        });
      } else {
        void createOrUpdateInternalFeedback({
          variables: { input: feedback },
          onCompleted: toastOptions.onCompleted,
          onError: toastOptions.onError,
        });
      }
    },
    [
      createOrUpdateExternalFeedback,
      createOrUpdateInternalFeedback,
      handleFeedbackUpdate,
      isExternal,
      toast,
    ]
  );

  return {
    existingFeedback,
    createOrUpdateFeedback,
    createOrUpdateFeedbackLoading: isExternal
      ? createOrUpdateExternalFeedbackLoading
      : createOrUpdateInternalFeedbackLoading,
  };
};
